import { observer } from 'mobx-react';
import { bind, ConnectionManager, IAccessTokenStore, IDisposable, ITermOptions, Term } from 'Mri.Web.Common';
import * as React from 'react';

export interface IDisplayTermProps {
    readonly serialNumber: string;
    readonly accessTokenStore: IAccessTokenStore;
}

@observer
export class DisplayTerm extends React.Component<IDisplayTermProps> {
    public constructor(props: IDisplayTermProps) {
        super(props);

        this.connectionManager = new ConnectionManager('/sockets/term', props.accessTokenStore);
        const { connection } = this.connectionManager;
        connection.on('OnTerminalCreated', this.onTerminalCreated);
        connection.on('Send', this.onSend);
        connection.onclose(this.onClose);
    }

    public async componentDidMount() {
        try {
            this.handle = await this.connectionManager.connect();
            await this.connectionManager.connection.send('CreateTerm', this.props.serialNumber, {
                cols: this.options.cols,
                rows: this.options.rows,
            });
        } catch (e: any) {
            console.error(e);
            if (this.term) this.term.write(JSON.stringify(e, undefined, '\r'));
        }
    }

    public async componentWillUnmount() {
        if (this.writeTimer) {
            window.clearTimeout(this.writeTimer);
            this.writeTimer = undefined;
        }

        if (this.pid) {
            try {
                await this.connectionManager.connection.send('Close', this.props.serialNumber, this.pid);
                this.handle?.dispose();
            } catch (e: any) {
                console.error(e);
            }

            this.pid = undefined;
        }
    }

    public render() {
        return <Term onData={this.onData} ref={this.setTerm} />;
    }

    @bind
    private setTerm(r: Term | null) {
        this.term = r;
    }

    @bind
    private onTerminalCreated(info: Mri.DisplayTerminals.TerminalInfo) {
        this.pid = info.pid;
    }

    @bind
    private async onSend(serialNumber: string, pid: string, message: string) {
        if (this.term && message) this.term.write(message);
    }

    @bind
    private onClose(e?: Error) {
        if (e) {
            console.error('Connection closed with error: ' + e);

            if (this.term) this.term.write(JSON.stringify(e, undefined, '\r'));
        } else {
            console.debug('Disconnected');
        }
    }

    @bind
    private onData(data: string) {
        if (this.writeBuffer) {
            this.writeBuffer += data;
        } else {
            this.writeBuffer = data;
            this.writeTimer = window.setTimeout(this.writeAsync, 50);
        }
    }

    @bind
    private async writeAsync(data: string) {
        if (this.writeTimer) {
            window.clearTimeout(this.writeTimer);
            this.writeTimer = undefined;
        }

        if (!this.term || !this.pid || !this.writeBuffer) return;

        const message = this.writeBuffer;
        this.writeBuffer = undefined;

        try {
            await this.connectionManager.connection.send('Send', this.props.serialNumber, this.pid, message);
        } catch (e: any) {
            console.error(e);
            if (this.term) this.term.write(JSON.stringify(e, undefined, '\r'));
        }
    }

    private readonly options: ITermOptions = {
        cols: 80,
        rows: 24,
    };

    private writeBuffer?: string;
    private writeTimer?: number;
    private term: Term | null = null;
    private pid?: string;
    private handle?: IDisposable;
    private readonly connectionManager: ConnectionManager;
}
